import type {RepositoryDataResponse} from '@github-ui/data-router-error-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {matchPath} from '@github-ui/react-router'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {IssueViewerPage} from '@github-ui/server-persisted-queries'
import type {IssueViewerPageQuery} from '@github-ui/server-persisted-queries/types/IssueViewerPage'

import {issuesAppBuilder} from '../config/app-builder'

// RepositoryDataResponse exposes the errors array so handleRepositoryDataError can detect FORBIDDEN/SAML.
export type IssueViewerPayload = RepositoryDataResponse<IssueViewerPageQuery>

export function buildIssueViewerRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('issueViewerRoute', {
    // The canonical issue URL. `issues#show` decides which stack serves it, so this route is only
    // registered when the same flag is on.
    path: '/:owner/:repo/issues/:number',
    queries: [
      mainQuery<IssueViewerPayload>({
        // owner/repo/number are resolved from the pathname by the BFF; only the persisted-query id
        // has to be forwarded.
        queryDeps: ({pathname}) => ({pathname, searchParams: {queryId: IssueViewerPage}}),
        persister: createServerPersistedQueryPersister({
          queryHash: IssueViewerPage,
          storageQueryKey: queryKey => {
            const queryDeps = queryKey[4]
            if (!isRecord(queryDeps) || typeof queryDeps.pathname !== 'string') return

            const match = matchPath('/:owner/:repo/issues/:number', queryDeps.pathname)
            const {owner, repo, number} = match?.params ?? {}
            if (!owner || !repo || !number || !/^\d+$/.test(number)) return

            const issueNumber = Number(number)
            if (!Number.isSafeInteger(issueNumber) || issueNumber <= 0) return

            return ['issue-viewer', 'primary-view', {number: issueNumber, owner, repo}]
          },
          maxAgeMs: DEFAULT_RELAY_CACHE_TTL,
          // An IndexedDB snapshot may be stale even within its TTL, so revalidate after restoring it.
          alwaysRefetch: true,
          // The router treats recent preloads as fresh, so they must not restore old snapshots.
          restoreOnPreload: false,
          // SSR uses setQueryData rather than a fetch; explicitly persist that initial response.
          persistInitialData: true,
          dataValidator: {name: 'issue-viewer-page', isValid: isCacheableIssueViewerQueryData},
          sendAnalyticsEvent: (eventType, target, payload) => sendEvent(eventType, {...payload, target}),
        }),
      }),
    ],
  })
}

function isCacheableIssueViewerQueryData(value: unknown): boolean {
  if (!isRecord(value) || !isRecord(value.payload)) return false

  const {meta, payload} = value
  if (meta !== undefined && (!isRecord(meta) || (meta.title !== undefined && typeof meta.title !== 'string'))) {
    return false
  }

  if (payload.errors != null && (!Array.isArray(payload.errors) || payload.errors.length > 0)) return false

  const data = payload.data
  if (!isRecord(data) || !isRecord(data.repository) || !isRecord(data.repository.issue)) return false

  return typeof data.repository.issue.id === 'string' && data.repository.issue.id.length > 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export const issueViewerRoute = buildIssueViewerRoute(issuesAppBuilder)
